import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { get } from '@lumapps/constants';
import { AppId } from '@lumapps/constants/app';
import { mdiArrowRight, mdiFullscreen, mdiFullscreenExit, mdiRefresh } from '@lumapps/lumx/icons';
import {
    Alignment,
    Button,
    ColorPalette,
    Emphasis,
    FlexBox,
    IconButton,
    Orientation,
    ProgressCircular,
    Size,
    Text,
    Typography,
} from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { generateUUID } from '@lumapps/utils/string/generateUUID';

import { LumappsComponentType } from '../../constants';
import type { LumappsComponentProps } from '../../types';
import type { IframeBaseMessage, IframeSettings } from './types';

import './index.scss';

export interface IframeComponentProps extends LumappsComponentProps<LumappsComponentType.Iframe, IframeSettings> {}

const IFRAME_ID = 'iframe-display';
const CLASSNAME = 'iframe-preview';

const Config = get();

/**
 * Iframe pattern that lets the user load an embedded content for a source url
 * @param IframeComponentProps
 * @returns IframeComponent
 */
export const IframeComponent: React.FC<IframeComponentProps> = ({
    componentProperties,
    isPreview,
    hasNextAction,
    onSkip,
    onSubmit,
}) => {
    const { block, element } = useClassnames(CLASSNAME);

    const { translateKey } = useTranslate();

    const [iframeKey, setIframeKey] = React.useState(generateUUID());

    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const fullscreenContainerRef = React.useRef<HTMLDivElement>(null);

    const isDevPortal = Config.applicationId === AppId.devPortal;

    const isMobile = Config.isMobileAppWebView || Config.applicationId === AppId.webview || isDevPortal;

    const loadingState = (overlay: boolean = false) => (
        <FlexBox
            className={element('progress', { overlay })}
            orientation={Orientation.vertical}
            hAlign={Alignment.center}
            vAlign={Alignment.center}
        >
            <ProgressCircular />
        </FlexBox>
    );

    const { settings: componentSettings } = componentProperties;

    const [isFullscreen, setIsFullscreen] = React.useState(componentSettings?.isDefaultFullScreen ?? false);

    const refreshIframe = () => {
        setIframeKey(generateUUID());
    };

    const toggleFullscreen = () => {
        if (!isFullscreen && isMobile && !isDevPortal) {
            fullscreenContainerRef.current?.requestFullscreen();
        } else if (isFullscreen && isMobile && !isDevPortal) {
            document.exitFullscreen();
            fullscreenContainerRef.current?.focus();
        }

        setIsFullscreen(!isFullscreen);
    };

    const heightDimensionDevPortal = isPreview ? '95%' : '500px';

    const iframeUrl = new URL(componentSettings?.url ?? '').origin;

    React.useEffect(() => {
        // Listen to messages coming from the iframe
        // Should only be load once
        const handleMessage = (event: MessageEvent) => {
            // 1. Security check: Only trust your own domain
            if (event.origin !== iframeUrl?.toLowerCase()) {
                return;
            }

            const { action, data } = event.data as IframeBaseMessage;

            // MANDATORY FILTER: Ignore if the message is not from the same source
            if (iframeRef.current?.contentWindow !== event.source) {
                return;
            }

            switch (action) {
                case 'close':
                    onSkip?.();
                    break;
                case 'next':
                    if (!data) {
                        return;
                    }
                    onSubmit?.(data);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [iframeUrl, onSkip, onSubmit]);

    if (!componentSettings?.url || componentSettings?.url?.length <= 0) {
        return loadingState();
    }

    return (
        <div
            ref={fullscreenContainerRef}
            tabIndex={-1}
            className={block({
                'is-fullscreen-web': isFullscreen && !isMobile,
                'is-fullscreen-mobile': isFullscreen && isMobile,
            })}
        >
            <div className={element('frame-overlay')} />
            <FlexBox
                className={block({
                    'is-fullscreen-web': isFullscreen && !isMobile,
                    'is-fullscreen-mobile': isFullscreen && isMobile,
                })}
                orientation={Orientation.vertical}
                gap={Size.tiny}
                hAlign={Alignment.center}
                vAlign={Alignment.center}
            >
                <FlexBox
                    orientation={Orientation.vertical}
                    className={element('container')}
                    style={
                        !isFullscreen
                            ? {
                                  width: isDevPortal ? '100%' : componentSettings?.width ?? '100%',
                                  height: isDevPortal ? heightDimensionDevPortal : componentSettings?.height ?? '100%',
                              }
                            : {}
                    }
                >
                    <FlexBox
                        orientation={Orientation.horizontal}
                        gap={Size.regular}
                        hAlign={Alignment.spaceBetween}
                        className={element('header')}
                    >
                        <Text
                            data-testid="iframe-title"
                            as="span"
                            typography={Typography.subtitle1}
                            truncate={{ lines: 2 }}
                        >
                            {componentSettings?.title || ''}
                        </Text>

                        <FlexBox orientation={Orientation.horizontal} gap={Size.tiny}>
                            <IconButton
                                label={translateKey(GLOBAL.REFRESH)}
                                emphasis={Emphasis.medium}
                                icon={mdiRefresh}
                                onClick={refreshIframe}
                                color={ColorPalette.dark}
                                hasBackground
                                size={isMobile && !isDevPortal ? Size.m : Size.s}
                            />
                            <IconButton
                                label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                                emphasis={Emphasis.medium}
                                icon={isFullscreen ? mdiFullscreenExit : mdiFullscreen}
                                onClick={toggleFullscreen}
                                color={ColorPalette.dark}
                                hasBackground
                                size={isMobile && !isDevPortal ? Size.m : Size.s}
                                disabled={isPreview}
                            />
                            {onSkip && hasNextAction && (
                                <Button
                                    emphasis={Emphasis.high}
                                    rightIcon={mdiArrowRight}
                                    onClick={onSkip}
                                    color={ColorPalette.primary}
                                    hasBackground
                                    size={isMobile && !isDevPortal ? Size.m : Size.s}
                                    disabled={isPreview}
                                >
                                    {translateKey(GLOBAL.NEXT)}
                                </Button>
                            )}
                        </FlexBox>
                    </FlexBox>

                    <div className={element('iframe-wrapper')}>
                        <iframe
                            ref={iframeRef}
                            id={IFRAME_ID}
                            key={iframeKey}
                            data-testid={iframeKey}
                            title={componentSettings?.title || 'Iframe'}
                            src={componentSettings?.url}
                            frameBorder="0"
                            allowFullScreen
                            width="100%"
                            height="100%"
                        />
                    </div>
                </FlexBox>
            </FlexBox>
        </div>
    );
};
